import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";

// -----------------------------------------------------------------------------
// useAiPrefetch — implements the "AI predicts next actions" branch of the
// Find2Near flow diagram: after every route change we background-fetch the
// endpoints the user is *likely* to hit next, warm the browser's HTTP cache
// (via a plain in-memory Map since Axios doesn't cache by default) and record
// click-through preferences to localStorage so the next visit reorders these
// predictions by observed behaviour.
//
// Rules
// -----
// • Never block the current render — every fetch runs after `requestIdleCallback`
//   or a 400 ms setTimeout fallback.
// • Respect Save-Data (navigator.connection.saveData). If the user is on a
//   metered connection, prefetch nothing.
// • Cap total prefetch payload per route change at 6 requests.
// • Cache TTL is 5 minutes — anything older is silently refetched next time.
// -----------------------------------------------------------------------------

// Predictions per current route → the endpoints the user "likely wants next".
// Order matters: higher-value items first because we truncate at 6 per burst.
// NOTE: `/categories` used to appear here for `/` and `/search`, but the
// backend has no such route — it produced a persistent 404 in the browser
// console. The homepage already knows its category list from the static
// `PUBLIC_GROUPS` table in Home.jsx, so nothing needs to be prefetched.
const PREDICTIONS = {
  "/":                ["/notices?limit=5", "/entertainment/platforms?kind=ott&region=global", "/software?limit=8", "/laws?kind=right"],
  "/search":          ["/entertainment/platforms?kind=ott&region=global"],
  "/notices":         ["/laws/meta", "/faqs/meta", "/cricket/live"],
  "/entertainment":   ["/cricket/live", "/entertainment/platforms?kind=music"],
  "/fuel":            ["/fuel-stations?limit=10", "/camps?active_now=true&limit=5"],
  "/camps":           ["/camps?limit=15", "/notices?category=disaster&limit=3"],
  "/laws":            ["/laws/meta", "/state-laws", "/legal-templates"],
  "/faqs":            ["/faqs/meta"],
  "/priority-card":   ["/priority-card/me", "/priority-card/hand2pay/history"],
  "/inbox":           ["/notices?limit=5"],
};

// A never-null preferences map: `{ "/search": {clicks: 12}, "/notices": {clicks: 3}, ... }`.
// This is the "AI learns user preferences" node in the diagram — a naive but
// effective frequency model that also survives a page refresh.
const PREF_KEY = "f2n:route_prefs:v1";
const loadPrefs = () => {
  try { return JSON.parse(localStorage.getItem(PREF_KEY) || "{}"); }
  catch { return {}; }
};
const savePrefs = (p) => { try { localStorage.setItem(PREF_KEY, JSON.stringify(p)); } catch { /* private mode */ } };

// Global lightweight cache the app can read (used by OfflineOverlay to show
// how many pages are cached) — also used to avoid duplicate prefetches within
// the same session.
if (typeof window !== "undefined") window.__f2n_cache = window.__f2n_cache || {};
const CACHE = typeof window !== "undefined" ? window.__f2n_cache : {};
const TTL_MS = 5 * 60 * 1000;

function isSlow() {
  if (typeof navigator === "undefined") return false;
  const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!c) return false;
  if (c.saveData) return true;
  return c.effectiveType === "slow-2g" || c.effectiveType === "2g";
}

async function prefetchOne(url) {
  const now = Date.now();
  const hit = CACHE[url];
  if (hit && (now - hit.at) < TTL_MS) return;   // still fresh
  try {
    const r = await api.get(url);
    CACHE[url] = { at: now, data: r.data };
  } catch { /* silent — background op */ }
}

function schedule(fn) {
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(fn, { timeout: 1500 });
  } else {
    setTimeout(fn, 400);
  }
}

export default function useAiPrefetch() {
  const loc = useLocation();
  const lastRouteRef = useRef(null);

  useEffect(() => {
    // Record the route change as a preference signal (skip duplicate fires on
    // hash-only / query-string-only navigations).
    if (lastRouteRef.current === loc.pathname) return;
    lastRouteRef.current = loc.pathname;

    const prefs = loadPrefs();
    prefs[loc.pathname] = { clicks: (prefs[loc.pathname]?.clicks || 0) + 1,
                            last_at: new Date().toISOString() };
    savePrefs(prefs);

    if (isSlow()) return;                    // respect Save-Data

    // Find the prediction bucket for this route. Fall back to `/` on unknown
    // paths so we still warm the app's canonical endpoints.
    const bucket = PREDICTIONS[loc.pathname]
        || PREDICTIONS[Object.keys(PREDICTIONS).find(k => k !== "/" && loc.pathname.startsWith(k))]
        || PREDICTIONS["/"];

    // Reorder the bucket by user preference weight — endpoints tied to routes
    // the user visits often get pulled first.
    const weighted = [...bucket].sort((a, b) => {
      const wa = prefs[a]?.clicks || 0;
      const wb = prefs[b]?.clicks || 0;
      return wb - wa;
    }).slice(0, 6);

    schedule(() => weighted.forEach(prefetchOne));
  }, [loc.pathname]);
}

// Also export a manual trigger — the Search page uses this when the user
// types a query, to warm nearby-related endpoints (parking, petrol, cafes)
// like the diagram's "User searches Restaurant" branch.
export function primeSearch(query, city) {
  if (isSlow() || !query?.trim()) return;
  const q = encodeURIComponent(query.trim());
  const c = city ? `&city=${encodeURIComponent(city)}` : "";
  schedule(() => {
    prefetchOne(`/search?q=${q}${c}&limit=10`);
    prefetchOne(`/fuel-stations?q=${q}${c}&limit=6`);
    prefetchOne(`/camps?q=${q}${c}&limit=4`);
  });
}
